import { memo, useRef, useState, useEffect, Fragment } from "react";
import isEqual from "lodash/isEqual";
import Draggable, { DraggableEvent, DraggableData } from "react-draggable";
import { Transition } from "@headlessui/react";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
// Local
import { ExistingCustomer } from "../../models/customer";
import LoadingSpinner from "../LoadingSpinner";
import { logger } from "../../logging";
import { phoneUtils } from "../../utils";
import { StyledTooltip } from "../StyledTooltip";
import { ButtonIconCreateJob } from "../ButtonIconCreateJob";
import { ButtonIconSearch } from "../ButtonIconSearch";
import { useStiltNav } from "../../navigation";
import { ExistingStiltPhoneCall } from "../../models/calls";

interface Props {
  siteKey: string;
  customer: ExistingCustomer | null;
  incomingCall: ExistingStiltPhoneCall | null;
  updateCall: () => Promise<void>;
  clearIncomingCall: () => void;
}

const dragIconStyles: React.CSSProperties = {
  display: "block",
  height: "26px",
  width: "22px",
};

/**
 * Memoized component for displaying the unmatched call bubble.
 *
 * Allows user to match a customer to an incoming call. Once the customer is
 * matched, the customer record is updated with the phone number, and this
 * component is switched out for BubbleActiveCall.
 */
const BubbleUnmatchedCall = memo(
  (props: Props) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    /** user knows that the bubble is draggable if they've dragged it before, we don't need to continuously show them the tooltip */
    const userKnows = useRef(false);
    const stiltNav = useStiltNav();
    const [updateStates, setUpdateStates] = useState({
      isBusy: false,
      isError: false,
    });

    useEffect(() => {
      const storedCoords = localStorage.getItem("callBubblePosition");
      if (storedCoords) {
        setPosition(JSON.parse(storedCoords));
        userKnows.current = true;
      }
    }, []);

    function handleStop(_e: DraggableEvent, data: DraggableData) {
      const newPos = { x: data.x, y: data.y };
      // intervene if drag handle leaves the browser window --
      if (newPos.y <= -75) newPos.y = -75; // TOP
      const content = document.getElementById("unmatchedDragSize");
      if (content) {
        const { width, height } = content.getBoundingClientRect();
        if (newPos.x >= window.innerWidth - width - 10) {
          newPos.x = window.innerWidth - width - 10; // RIGHT
        }
        if (newPos.y > window.innerHeight - height) {
          newPos.y = window.innerHeight - height; // BOTTOM
        }
        if (newPos.x < -width) newPos.x = -width; // LEFT

        newPos.y = Math.round(newPos.y);
        newPos.x = Math.round(newPos.x);
      } else {
        logger.error("Expected element with id 'unmatchedDragSize' not found");
      }

      userKnows.current = true;
      setPosition(newPos);
      setIsDragging(false);
      localStorage.setItem("callBubblePosition", JSON.stringify(newPos));
    }

    const dragHandleButton = (
      <button
        className={`${isDragging ? "cursor-grabbing" : "cursor-grab"} rotate-90 rounded-lg bg-amber-800 text-white`}
        aria-label="click and drag to move the call bubble"
      >
        <DragIndicatorIcon style={dragIconStyles} />
      </button>
    );

    const searchButton = (
      <StyledTooltip title="Find a match by searching all customers">
        <ButtonIconSearch
          onClick={() => stiltNav.customerSearchPage({ query: "" })}
          overrideButtonClasses="rounded-full text-amber-800 bg-amber-50 transition-colors border border-amber-800/60 hover:bg-white"
          overrideIconStyles={{
            padding: "4px",
            height: "30px",
            width: "30px",
          }}
        />
      </StyledTooltip>
    );

    const createJobButton = (
      <StyledTooltip title="Find a match while creating a job">
        <ButtonIconCreateJob
          onClick={() => stiltNav.createTaskForCustomer({})}
          overrideButtonClasses="rounded-full text-amber-800 bg-amber-50 transition-colors border border-amber-800/60 hover:bg-white"
          overrideIconStyles={{ height: "32px", width: "32px" }}
        />
      </StyledTooltip>
    );

    function getTruncatedCustomerName(name: string): string {
      return name.length > 38 ? name.slice(0, 35) + "..." : name;
    }

    // Render the match customer prompt when a customer is selected
    const renderMatchCustomerPrompt = () => {
      if (!props.customer) return null;
      return (
        <div className="min-w-48 text-center text-base">
          <p className="whitespace-nowrap">Customer match needed</p>
          <p className="mt-1">
            {getTruncatedCustomerName(props.customer.name)}
          </p>
          {props.incomingCall?.caller && (
            <p className="text-sm font-medium">
              {phoneUtils.display(props.incomingCall.caller)}
            </p>
          )}
          <div className="relative mb-3 mt-2 flex justify-center">
            <button
              onClick={async () => {
                try {
                  setUpdateStates({ isBusy: true, isError: false });
                  await props.updateCall();
                  setUpdateStates({ isBusy: false, isError: false });
                } catch (e) {
                  logger.error(
                    `Could not update call document for ${props.incomingCall?.caller}`,
                  );
                  setUpdateStates({ isBusy: false, isError: true });
                }
              }}
              disabled={updateStates.isBusy}
              className="rounded-full border-2 border-amber-800/80 bg-amber-50 px-3.5 py-1 text-sm text-amber-800 transition-colors hover:bg-white"
            >
              {updateStates.isBusy ? (
                <div className="flex items-center">
                  <LoadingSpinner marginClass="-ml-1 mr-2" />
                  <p>Matching...</p>
                </div>
              ) : (
                "Match Customer"
              )}
            </button>

            <span className="absolute -bottom-5 -right-2.5">
              {searchButton}
            </span>
          </div>
        </div>
      );
    };

    // Render the select customer prompt when no customer is selected
    const renderSelectCustomerPrompt = () => {
      return (
        <>
          <span className="mb-1 inline-block min-w-48 text-center">
            <p className="whitespace-nowrap">Customer match needed</p>
            {props.incomingCall?.caller && (
              <p className="text-sm font-medium">
                {phoneUtils.display(props.incomingCall.caller)}
              </p>
            )}
          </span>
          <div className="flex items-center justify-center gap-6">
            {searchButton}
            {createJobButton}
          </div>
        </>
      );
    };

    const renderErrorState = () => {
      if (!updateStates.isError) return null;
      return (
        <div className="min-w-48 text-center text-sm">
          <p className="text-base">Error updating call record</p>
          <p>Click dismiss and reselect the call to try again.</p>
          <button
            onClick={props.clearIncomingCall}
            className="mt-2 rounded-full border border-amber-800/60 bg-amber-50 px-3.5 py-1 text-amber-800 transition-colors hover:bg-white"
          >
            Dismiss
          </button>
        </div>
      );
    };

    // If no incoming call and no error, don't render anything
    if (!props.incomingCall && !updateStates.isError) return <></>;

    return (
      <Draggable
        nodeRef={nodeRef}
        handle=".dragHandle"
        position={position}
        defaultPosition={{ x: 0, y: 0 }}
        onStart={() => setIsDragging(true)}
        onStop={handleStop}
      >
        <Transition appear show={true} as="div" ref={nodeRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div
              id="unmatchedDragSize"
              className={`${
                isDragging ? "shadow-darker" : "shadow-elevation"
              } inline-block w-fit max-w-60 transform rounded-xl border-2 border-amber-600 bg-amber-100 px-3 pb-1 pt-2 text-amber-900 ring-1 ring-white transition-all`}
            >
              {/*
              - unmatchedDragSize id exists so we can ensure the drag handle stays within the screen bounds
              - dragHandle div has some padding on it so the target is larger
              - omitting the tooltip if the user is aware that the bubble is draggable
              */}
              <div
                className={`dragHandle p-2 pb-0 ${isDragging ? "cursor-grabbing" : "cursor-grab"} absolute -right-3 -top-[22px]`}
              >
                {userKnows ? (
                  dragHandleButton
                ) : (
                  <StyledTooltip title="Drag to move">
                    {dragHandleButton}
                  </StyledTooltip>
                )}
              </div>

              {renderErrorState()}
              {props.incomingCall &&
                props.customer &&
                renderMatchCustomerPrompt()}
              {props.incomingCall &&
                !props.customer &&
                renderSelectCustomerPrompt()}
            </div>
          </Transition.Child>
        </Transition>
      </Draggable>
    );
  },
  (prev, next) => isEqual(prev.customer, next.customer),
);

export default BubbleUnmatchedCall;
